import React, { FC, useRef, useState } from 'react';
import { Box, IconButton, TextField, Typography, Paper } from '@mui/material';
import { Add, Check, Close } from '@mui/icons-material';
import { FirebaseError } from 'firebase/app';
import { useSnackbar } from 'notistack';
import { errorMessages } from 'data/messages/messages';
import Button from '../../../Base/Button';
import { useAddTaskMutation } from '../../../../redux/apiSlices/tasks.slice';

const AddTask: FC<{
  columnId: string;
  order: number;
  boardId: string;
  projectId: string;
  color: string;
}> = ({ columnId, order, projectId, boardId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const inputRef = useRef<HTMLInputElement>();
  const [editMode, setEditMode] = useState(false);
  const [addTask, { isLoading }] = useAddTaskMutation();

  const openEditMode = () => {
    setEditMode(true);
  };
  const closeEditMode = () => {
    setEditMode(false);
  };
  const onInputBlur = (event: React.FocusEvent) => {
    // Don't close if clicking on the confirm/cancel buttons
    if (event.relatedTarget?.closest('.add-task-actions')) return;
    if (!inputRef.current?.value) closeEditMode();
  };
  const onAddTask = async () => {
    try {
      const title = inputRef.current?.value;
      if (title) {
        await addTask({
          task: { title, columnId, order, projectId, boardId },
        });
        // Clear input for adding another task
        if (inputRef.current) {
          inputRef.current.value = '';
          inputRef.current.focus();
        }
      }
    } catch (err) {
      const error = err as FirebaseError;
      enqueueSnackbar(error.message || errorMessages.default, {
        variant: 'error',
      });
    }
  };
  const onEnter = async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      await onAddTask();
    }
    if (event.key === 'Escape') {
      closeEditMode();
    }
  };

  return (
    <>
      {editMode ? (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'primary.main',
            boxShadow: '0 0 0 2px rgba(29, 78, 216, 0.1)',
          }}
        >
          <TextField
            size="small"
            autoFocus
            inputRef={inputRef}
            fullWidth
            placeholder="What needs to be done?"
            onKeyDown={onEnter}
            onBlur={onInputBlur}
            multiline
            maxRows={3}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'transparent',
                '& fieldset': {
                  border: 'none',
                },
                '& input, & textarea': {
                  p: 0,
                  fontSize: '0.875rem',
                },
              },
            }}
          />
          <Box
            className="add-task-actions"
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 0.5,
              mt: 1,
            }}
          >
            <IconButton
              size="small"
              onClick={closeEditMode}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'error.main' },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={onAddTask}
              disabled={isLoading}
              sx={{
                color: 'primary.main',
                backgroundColor: '#EFF6FF',
                '&:hover': {
                  backgroundColor: '#DBEAFE',
                },
              }}
            >
              <Check fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      ) : (
        <Button
          loading={isLoading}
          startIcon={<Add sx={{ fontSize: 18 }} />}
          fullWidth
          onClick={openEditMode}
          sx={{
            justifyContent: 'flex-start',
            color: 'text.secondary',
            fontWeight: 500,
            py: 1,
            px: 1.5,
            borderRadius: 2,
            backgroundColor: 'transparent',
            border: '1px dashed',
            borderColor: 'divider',
            '&:hover': {
              backgroundColor: 'action.hover',
              borderColor: 'primary.main',
              color: 'primary.main',
            },
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            Add a task
          </Typography>
        </Button>
      )}
    </>
  );
};

export default AddTask;
